import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getNotifications, markNotificationRead, markAllNotificationsRead, getTodaysBirthdays, getBranding, getSystemSettings } from '../lib/api';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [birthdayUsers, setBirthdayUsers] = useState([]);
  const [showBirthdayPopup, setShowBirthdayPopup] = useState(false);
  const [branding, setBranding] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await getNotifications();
      // Only show unread notifications (like a phone)
      const unread = response.data.filter(n => {
        const readBy = n.is_read_by || [];
        return readBy.length === 0;
      });
      setNotifications(unread);
      setUnreadCount(unread.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchBirthdays = useCallback(async () => {
    try {
      const response = await getTodaysBirthdays();
      if (response.data.length > 0) {
        setBirthdayUsers(response.data);
        const shownToday = localStorage.getItem('birthdayPopupShown');
        const today = new Date().toDateString();
        if (shownToday !== today) {
          setShowBirthdayPopup(true);
          localStorage.setItem('birthdayPopupShown', today);
        }
      }
    } catch (error) {
      console.error('Error fetching birthdays:', error);
    }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const response = await getBranding();
      setBranding(response.data);
      if (response.data?.primary_color) {
        document.documentElement.style.setProperty('--brand-primary', response.data.primary_color);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    }
  }, []);

  const fetchSystemSettings = useCallback(async () => {
    try {
      const response = await getSystemSettings();
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchNotifications();
      fetchBirthdays();
      fetchBranding();
      fetchSystemSettings();
      
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchNotifications, fetchBirthdays, fetchBranding, fetchSystemSettings]);

  const markAsRead = async (notificationId) => {
    try {
      // Optimistic update: remove immediately from UI
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
      await markNotificationRead(notificationId);
    } catch (error) {
      // Rollback on failure
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    try {
      // Optimistic: clear all unread
      setNotifications([]);
      setUnreadCount(0);
      await markAllNotificationsRead();
    } catch (error) {
      fetchNotifications();
    }
  };

  const showPopupNotification = (title, message, type = 'info') => {
    toast[type === 'error' ? 'error' : type === 'success' ? 'success' : 'info'](title, {
      description: message,
      duration: 5000,
    });
  };

  const closeBirthdayPopup = () => {
    setShowBirthdayPopup(false);
  };

  // Currency formatter based on system settings
  const formatCurrency = (amount) => {
    if (!systemSettings) {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    }
    
    const { 
      currency_symbol, 
      currency_position, 
      decimal_separator, 
      thousands_separator, 
      decimal_places 
    } = systemSettings;
    
    const absAmount = Math.abs(amount || 0);
    const isNegative = amount < 0;
    
    // Format number
    const parts = absAmount.toFixed(decimal_places).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands_separator);
    const decimalPart = parts[1];
    
    let formatted = decimal_places > 0 
      ? `${integerPart}${decimal_separator}${decimalPart}`
      : integerPart;
    
    // Add currency symbol
    if (currency_position === 'before') {
      formatted = `${currency_symbol}${formatted}`;
    } else {
      formatted = `${formatted}${currency_symbol}`;
    }
    
    return isNegative ? `-${formatted}` : formatted;
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      birthdayUsers,
      showBirthdayPopup,
      branding,
      systemSettings,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      showPopupNotification,
      closeBirthdayPopup,
      refreshBranding: fetchBranding,
      refreshSystemSettings: fetchSystemSettings,
      formatCurrency
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
